import { useState, useEffect, type MouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Home,
  DollarSign,
  Building2,
  Building,
  Users,
  User,
  MessageCircle,
  Sun,
  Moon,
  Bell,
  Eye,
  TrendingUp,
  Menu,
  HardDrive,
  X,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
  CreditCard,
  BookOpen,
  Crown,
  Tag,
  Receipt
} from 'lucide-react'
import { createClient } from '@supabase/supabase-js'
import { useTheme } from '../contexts/ThemeContext'
import { useAssinatura } from '../hooks/useAssinatura'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import { useCheckSubscription } from '../hooks/useCheckSubscription'


// Configuração do Supabase
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabase = createClient(supabaseUrl, supabaseKey)

interface DashboardLayoutProps {
  children: React.ReactNode
  isModalOpen?: boolean
}

export default function DashboardLayout({ children, isModalOpen = false }: DashboardLayoutProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobileProfileMenuOpen, setIsMobileProfileMenuOpen] = useState(false)
  const [isDesktopProfileMenuOpen, setIsDesktopProfileMenuOpen] = useState(false)
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null)
  const location = useLocation()
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()
  const { loading: assinaturaLoading, getAssinaturaStatus } = useAssinatura()
  const assinaturaStatus = getAssinaturaStatus()
  const { isAuthenticated: isAdmin } = useAdminAuth()

  // Verificar se o usuário tem assinatura - se não tiver, redireciona para /assinar
  useCheckSubscription()

  // Função auxiliar para verificar se o acesso está permitido
  const temAcessoPermitido = () => {
    if (assinaturaLoading) return false
    // Permite acesso se estiver ativa ou em trial não expirado
    return assinaturaStatus.status === 'ativa' ||
      (assinaturaStatus.status === 'trial' && !assinaturaStatus.trial_expirado)
  }

  const handleVendasClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleImoveisClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleConstrutorasClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleProprietariosClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleRelatoriosClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleMensagensClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleMeuDriverClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleCursosClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  const handleAfiliadosClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!temAcessoPermitido()) {
      event.preventDefault()
      event.stopPropagation()
      navigate('/assinar')
      return
    }

    setIsMobileMenuOpen(false)
  }

  // Verificar se está logado
  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        setUser(session.user)
      } else {
        navigate('/login')
      }
    }
    checkUser()
  }, [navigate])

  // Fechar menu mobile com ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMobileMenuOpen(false)
      }
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [])






  // Fechar menu de perfil quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as HTMLElement

      // Verificar menu mobile
      if (isMobileProfileMenuOpen) {
        const mobileProfileMenus = document.querySelectorAll('.profile-menu-container')
        const mobileProfileButton = mobileProfileMenus[0] // Primeiro é o mobile

        if (mobileProfileButton) {
          const isClickInsideMenu = mobileProfileButton.contains(target)
          const isClickOnButton = mobileProfileButton.querySelector('button')?.contains(target)

          if (!isClickInsideMenu && !isClickOnButton) {
            setIsMobileProfileMenuOpen(false)
          }
        }
      }

      // Verificar menu desktop
      if (isDesktopProfileMenuOpen) {
        const desktopProfileMenus = document.querySelectorAll('.profile-menu-container')
        const desktopProfileButton = desktopProfileMenus[1] // Segundo é o desktop

        if (desktopProfileButton) {
          const isClickInsideMenu = desktopProfileButton.contains(target)
          const isClickOnButton = desktopProfileButton.querySelector('button')?.contains(target)

          if (!isClickInsideMenu && !isClickOnButton) {
            setIsDesktopProfileMenuOpen(false)
          }
        }
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [isMobileProfileMenuOpen, isDesktopProfileMenuOpen])

  const toggleMobileProfileMenu = () => {
    setIsMobileProfileMenuOpen(!isMobileProfileMenuOpen)
  }

  const toggleDesktopProfileMenu = () => {
    setIsDesktopProfileMenuOpen(!isDesktopProfileMenuOpen)
  }

  const handleProfileClick = () => {
    navigate('/perfil')
    setIsMobileProfileMenuOpen(false)
    setIsDesktopProfileMenuOpen(false)
  }

  const handleSettingsClick = () => {
    navigate('/configuracoes')
    setIsMobileProfileMenuOpen(false)
    setIsDesktopProfileMenuOpen(false)
  }

  const handleFinanceiroClick = () => {
    navigate('/financeiro')
    setIsMobileProfileMenuOpen(false)
    setIsDesktopProfileMenuOpen(false)
  }

  const handleAssinaturaClick = () => {
    navigate('/assinatura')
    setIsMobileProfileMenuOpen(false)
    setIsDesktopProfileMenuOpen(false)
  }

  const handleLogoutClick = async () => {
    await supabase.auth.signOut()
    navigate('/login')
    setIsMobileProfileMenuOpen(false)
    setIsDesktopProfileMenuOpen(false)
  }

  const isActive = (path: string) => {
    return location.pathname === path
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
        {/* Mobile Header */}
        <div className={`lg:hidden fixed top-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between transition-all duration-300 ${isModalOpen ? 'blur-sm opacity-50' : ''}`}>
          <div className="flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <Menu size={20} />
            </button>
            <img
              src={isDark ? "/logo-modo-dark.png" : "/logo-fundo-claro.png"}
              alt="Latera"
              className="ml-3 h-8 w-auto"
            />
          </div>

          <div className="flex items-center gap-3">
            <button className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <Bell size={20} />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title={isDark ? 'Ativar modo claro' : 'Ativar modo escuro'}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            {/* Profile Menu */}
            <div className="relative profile-menu-container">
              <button
                onClick={toggleMobileProfileMenu}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                style={{ backgroundColor: 'rgb(220, 95, 50)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(200, 85, 40)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(220, 95, 50)'}
                title="Perfil do Corretor"
              >
                <User size={16} className="text-white" />
              </button>

              {/* Profile Menu Popup */}
              {isMobileProfileMenuOpen && (
                <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 min-w-48 w-64">
                  {/* Seção Principal */}
                  <div className="px-2 py-1">
                    <div className="flex items-center px-2 py-2 text-sm text-gray-500 dark:text-gray-400 font-medium">
                      <User size={14} className="mr-2" />
                      {user?.email}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

                  {/* Opções do Corretor */}
                  <div className="px-2 py-1">
                    <button
                      onClick={handleProfileClick}
                      className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                    >
                      <User size={16} />
                      <span>Meu Perfil</span>
                    </button>

                    <button
                      onClick={handleSettingsClick}
                      className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                    >
                      <Settings size={16} />
                      <span>Configurações</span>
                    </button>

                    <button
                      onClick={handleAssinaturaClick}
                      className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                    >
                      <Crown size={16} />
                      <span>Assinatura</span>
                    </button>

                    <button
                      onClick={handleFinanceiroClick}
                      className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                    >
                      <CreditCard size={16} />
                      <span>Financeiro</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

                  {/* Logout */}
                  <div className="px-2 py-1">
                    <button
                      onClick={handleLogoutClick}
                      className="w-full px-2 py-2 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex items-center space-x-2 text-sm"
                    >
                      <LogOut size={16} />
                      <span>Sair</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={`
        fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:flex-shrink-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isSidebarCollapsed ? 'lg:w-20 w-64' : 'w-64'}
        ${isModalOpen ? 'blur-sm opacity-50' : ''}
      `}>
          {/* Mobile Close Button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Menu</h2>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-4 lg:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
                {isSidebarCollapsed ? (
                  <div className="flex flex-col items-center space-y-2">
                    <img
                      src="/icone.png"
                      alt="Latera"
                      className="h-8 w-8"
                    />
                    <button
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                      className="hidden lg:flex p-1 text-gray-500 dark:text-gray-400 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center">
                      <img
                        src={isDark ? "/logo-modo-dark.png" : "/logo-fundo-claro.png"}
                        alt="Latera"
                        className="h-8 w-auto mb-1"
                      />

                    </div>
                    <button
                      onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                      className="hidden lg:flex p-2 text-gray-500 dark:text-gray-400 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ChevronLeft size={20} />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className={`flex-1 overflow-y-auto ${isSidebarCollapsed ? 'p-2 lg:p-3' : 'p-4 lg:p-6'}`}>
              <div className={isSidebarCollapsed ? 'space-y-2' : 'space-y-6'}>
                {!isSidebarCollapsed && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 dark:text-gray-400 uppercase tracking-wide mb-3">
                      Menu Principal
                    </div>
                  </div>
                )}

                <div className={isSidebarCollapsed ? 'space-y-2' : 'space-y-1'}>
                  <Link
                    to="/dashboard"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/dashboard')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    title={isSidebarCollapsed ? 'Dashboard' : ''}
                  >
                    <Home size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/dashboard') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && (
                      <>
                        <span className={`text-sm font-medium ${isActive('/dashboard') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`}>
                          Dashboard
                        </span>
                        <TrendingUp size={12} className="ml-auto text-gray-500 dark:text-gray-400 dark:text-gray-400" />
                      </>
                    )}
                  </Link>

                  <Link
                    to="/vendas"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/vendas')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleVendasClick}
                    title={isSidebarCollapsed ? 'Vendas' : ''}
                  >
                    <DollarSign size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/vendas') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/vendas') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`}>Vendas</span>}
                  </Link>

                  <Link
                    to="/imoveis"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/imoveis')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleImoveisClick}
                    title={isSidebarCollapsed ? 'Imóveis' : ''}
                  >
                    <Building size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/imoveis') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && (
                      <span className={`text-sm font-medium ${isActive('/imoveis') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`}>
                        Imóveis
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/construtoras"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/construtoras')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleConstrutorasClick}
                    title={isSidebarCollapsed ? 'Construtoras' : ''}
                  >
                    <Building2 size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/construtoras') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/construtoras') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`}>Construtoras</span>}
                  </Link>

                  <Link
                    to="/proprietarios"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/proprietarios')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleProprietariosClick}
                    title={isSidebarCollapsed ? 'Proprietários' : ''}
                  >
                    <Users size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/proprietarios') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && (
                      <span className={`text-sm font-medium ${isActive('/proprietarios') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 dark:text-gray-400'}`}>
                        Proprietários
                      </span>
                    )}
                  </Link>

                  <Link
                    to="/relatorios"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/relatorios')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleRelatoriosClick}
                    title={isSidebarCollapsed ? 'Relatórios' : ''}
                  >
                    <Eye size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/relatorios') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/relatorios') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Relatórios</span>}
                  </Link>

                  <Link
                    to="/mensagens"
                    className={`flex items-center rounded-lg transition-colors relative ${isActive('/mensagens')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleMensagensClick}
                    title={isSidebarCollapsed ? 'Mensagens (3 novas)' : ''}
                  >
                    <MessageCircle size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/mensagens') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && (
                      <>
                        <span className={`text-sm font-medium ${isActive('/mensagens') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Mensagens</span>
                        <div className="ml-auto text-xs bg-red-600 text-white px-2 py-1 rounded-full">3</div>
                      </>
                    )}
                    {isSidebarCollapsed && (
                      <div className="absolute -top-1 -right-1 text-xs bg-red-600 text-white px-1.5 py-0.5 rounded-full">3</div>
                    )}
                  </Link>

                  {/* Separador Ferramentas */}
                  {!isSidebarCollapsed && (
                    <div className="px-3 py-2">
                      <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        Ferramentas
                      </div>
                    </div>
                  )}

                  {/* Meu Driver */}
                  <Link
                    to="/meu-driver"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/meu-driver')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleMeuDriverClick}
                    title={isSidebarCollapsed ? 'Meu Driver' : ''}
                  >
                    <HardDrive size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/meu-driver') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/meu-driver') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Meu Driver</span>}
                  </Link>

                  {/* Afiliados */}
                  <Link
                    to="/afiliados"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/afiliados')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleAfiliadosClick}
                    title={isSidebarCollapsed ? 'Afiliados' : ''}
                  >
                    <Users size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/afiliados') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/afiliados') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Afiliados</span>}
                  </Link>

                  {/* Cursos */}
                  <Link
                    to="/cursos"
                    className={`flex items-center rounded-lg transition-colors ${isActive('/cursos')
                      ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                    onClick={handleCursosClick}
                    title={isSidebarCollapsed ? 'Cursos' : ''}
                  >
                    <BookOpen size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/cursos') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                    {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/cursos') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Cursos</span>}
                  </Link>

                  {/* Links Admin - Apenas visíveis para administradores */}
                  {isAdmin && (
                    <>
                      {/* Admin Cursos */}
                      <Link
                        to="/admin/cursos"
                        className={`flex items-center rounded-lg transition-colors ${isActive('/admin/cursos')
                          ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        title={isSidebarCollapsed ? 'Admin Cursos' : ''}
                      >
                        <Settings size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/admin/cursos') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                        {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/admin/cursos') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Admin Cursos</span>}
                      </Link>

                      {/* Admin Cupons */}
                      <Link
                        to="/admin/cupons"
                        className={`flex items-center rounded-lg transition-colors ${isActive('/admin/cupons')
                          ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        title={isSidebarCollapsed ? 'Admin Cupons' : ''}
                      >
                        <Tag size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/admin/cupons') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                        {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/admin/cupons') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Admin Cupons</span>}
                      </Link>

                      {/* Controle de Assinaturas */}
                      <Link
                        to="/admin/assinaturas"
                        className={`flex items-center rounded-lg transition-colors ${isActive('/admin/assinaturas')
                          ? 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          } ${isSidebarCollapsed ? 'justify-center py-3 px-2 mx-1' : 'p-3'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                        title={isSidebarCollapsed ? 'Controle de Assinaturas' : ''}
                      >
                        <Receipt size={isSidebarCollapsed ? 26 : 20} className={`${isSidebarCollapsed ? '' : 'mr-3'} ${isActive('/admin/assinaturas') ? 'text-[#eb6a3b] dark:text-[#eb6a3b]' : 'text-gray-500 dark:text-gray-400'}`} />
                        {!isSidebarCollapsed && <span className={`text-sm font-medium ${isActive('/admin/assinaturas') ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>Controle de Assinaturas</span>}
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Mobile Overlay */}
        {isMobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className={`flex-1 flex flex-col min-h-screen lg:pt-0 pt-16 transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
          }`}>
          {/* Desktop Header */}
          <header className={`hidden lg:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 sticky top-0 z-30 transition-all duration-300 ${isModalOpen ? 'blur-sm opacity-50' : ''}`}>
            <div className="flex items-center justify-between">
              <div>

                <p className="text-gray-500 dark:text-gray-400 dark:text-gray-400">Bem-vindo de volta, {user.email}</p>
              </div>

              <div className="flex items-center gap-4">
                <button className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <Bell size={20} />
                </button>

                {/* Theme Toggle Button */}
                <button
                  onClick={toggleTheme}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title={isDark ? 'Modo claro' : 'Modo escuro'}
                >
                  {isDark ? <Sun size={20} /> : <Moon size={20} />}
                </button>

                {/* Profile Menu */}
                <div className="relative profile-menu-container">
                  <button
                    onClick={toggleDesktopProfileMenu}
                    className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                    style={{ backgroundColor: 'rgb(220, 95, 50)' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgb(200, 85, 40)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgb(220, 95, 50)'}
                    title="Perfil do Corretor"
                  >
                    <User size={16} className="text-white" />
                  </button>

                  {/* Profile Menu Popup */}
                  {isDesktopProfileMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50 min-w-48 w-64">
                      {/* Seção Principal */}
                      <div className="px-2 py-1">
                        <div className="flex items-center px-2 py-2 text-sm text-gray-500 dark:text-gray-400 font-medium">
                          <User size={14} className="mr-2" />
                          {user?.email}
                        </div>
                      </div>

                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

                      {/* Opções do Corretor */}
                      <div className="px-2 py-1">
                        <button
                          onClick={handleProfileClick}
                          className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                        >
                          <User size={16} />
                          <span>Meu Perfil</span>
                        </button>

                        <button
                          onClick={handleSettingsClick}
                          className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                        >
                          <Settings size={16} />
                          <span>Configurações</span>
                        </button>

                        <button
                          onClick={handleAssinaturaClick}
                          className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                        >
                          <Crown size={16} />
                          <span>Assinatura</span>
                        </button>

                        <button
                          onClick={handleFinanceiroClick}
                          className="w-full px-2 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md flex items-center space-x-2 text-sm"
                        >
                          <CreditCard size={16} />
                          <span>Financeiro</span>
                        </button>
                      </div>

                      <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>

                      {/* Logout */}
                      <div className="px-2 py-1">
                        <button
                          onClick={handleLogoutClick}
                          className="w-full px-2 py-2 text-left text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md flex items-center space-x-2 text-sm"
                        >
                          <LogOut size={16} />
                          <span>Sair</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 bg-gray-50 dark:bg-gray-900">
            {children}
          </main>
        </div>
      </div>
    </>
  )
}